import React, {useEffect} from 'react';
import WarningModal from 'components/WarningModal/WarningModal';
import {useForm} from 'react-hook-form';
import {ErrorMessage} from '@hookform/error-message';
import styles from './CampaignModalInputs.module.scss';
import {useUpdateCampaignMutation} from 'store/slices/campaignSlice';
import {CampaignDto} from 'store/models/campaign';

interface Props {
  open: boolean;
  onClose: () => void;
  campaign: CampaignDto | null;
  onSuccess: (msg: string) => void;
}

export default function EditCampaignModal({
  open,
  onClose,
  campaign,
  onSuccess,
}: Props) {
  const [updateCampaign, {isLoading}] = useUpdateCampaignMutation();

  const {
    register,
    handleSubmit,
    formState: {errors},
    reset,
  } = useForm({
    defaultValues: {name: '', link: '', imageUrl: ''},
  });

  useEffect(() => {
    if (open && campaign)
      reset({
        name: campaign.name,
        link: campaign.link,
        imageUrl: campaign.imageUrl,
      });
  }, [open, campaign, reset]);

  const submit = async (data: any) => {
    await updateCampaign({
      campaignId: campaign!.id,
      ...data,
    }).unwrap();

    onSuccess('Campaign updated.');
    onClose();
  };

  if (!open || !campaign) return null;

  return (
    <WarningModal
      isOpen={open}
      onClose={onClose}
      entityText={campaign.name}
      title="Edit campaign"
      textBlocks={[]}
      confirmButtonText="Save"
      onConfirmButtonClick={handleSubmit(submit)}
      loading={isLoading}
    >
      <div className={styles.inputsWrapper}>
        <div className={styles.inputContainer}>
          <label>Name</label>
          <input
            {...register('name', {required: 'Name is required'})}
            className={`${styles.input} ${
              errors?.name ? styles.inputError : ''
            }`}
          />
          <ErrorMessage
            errors={errors}
            name="name"
            render={({message}) => (
              <span className={styles.inputErrorMessage}>{message}</span>
            )}
          />
        </div>

        <div className={styles.inputContainer}>
          <label>Link</label>
          <input
            {...register('link', {required: 'Link is required'})}
            className={`${styles.input} ${
              errors?.link ? styles.inputError : ''
            }`}
          />
          <ErrorMessage
            errors={errors}
            name="link"
            render={({message}) => (
              <span className={styles.inputErrorMessage}>{message}</span>
            )}
          />
        </div>

        <div className={styles.inputContainer}>
          <label>Image URL</label>
          <input
            {...register('imageUrl')}
            className={`${styles.input} ${
              errors?.imageUrl ? styles.inputError : ''
            }`}
          />
          <ErrorMessage
            errors={errors}
            name="imageUrl"
            render={({message}) => (
              <span className={styles.inputErrorMessage}>{message}</span>
            )}
          />
        </div>
      </div>
    </WarningModal>
  );
}
